/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useEffect } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { IoIosArrowBack } from 'react-icons/io';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import {
  ButtonAdd,
  Input,
  Layout,
  Modal,
  Select,
  ButtonBlack,
  ButtonYellow,
} from '~/components';
import { useTrainingStore } from '~/hooks/trainingStore';
import api from '~/services/api';
import getValidationErrors from '~/utils/getValidationErrors';

import { Accessory } from './Accessory';
import { ActivePrinciple } from './ActivePrinciple';
import { AerobicCardio } from './AerobicCardio';
import { Equipment } from './Equipment';
import { HandPrint } from './HandPrint';
import { NumberOfSeries } from './NumberOfSeries';
import { Posture } from './Posture';
import { Segment } from './Segment';

import {
  Header,
  HeaderWrapper,
  HeaderLabel,
  Sidebar,
  Container,
  Wrapper,
  CreateContainer,
  Content,
} from './styles';

type SelectOption = {
  value: string;
  label: string;
};

export function TechnicalData() {
  const { goBack } = useHistory();
  const { categoryData, updateCategory, getAdminCategory } = useTrainingStore();

  const [selectedComponent, setSelectedComponent] = useState('SEGMENTO');
  const [newModal, setNewModal] = useState(false);
  const [showAdminExercises, setShowAdminExercises] = useState(false);

  useEffect(() => {
    if (showAdminExercises) {
      getAdminCategory(selectedComponent);
    } else {
      updateCategory(selectedComponent);
    }
  }, [updateCategory, selectedComponent, showAdminExercises, getAdminCategory]);

  const sidebarList = [
    {
      value: 'SEGMENTO',
      title: 'Segmento',
    },
    {
      value: 'EQUIPAMENTO',
      title: 'Equipamento',
    },
    {
      value: 'ACESSÓRIO',
      title: 'Acessório',
    },
    {
      value: 'POSIÇÃO MÃOS / PÉS',
      title: 'Posição mãos/pés',
    },
    {
      value: 'POSTURA',
      title: 'Postura',
    },
    {
      value: 'PRINCIPIO ATIVO',
      title: 'Princípio ativo',
    },
    {
      value: 'N° SÉRIES',
      title: 'N° de séries',
    },
    {
      value: 'CARDIO AEROBICO',
      title: 'Cárdio aeróbico',
    },
  ];

  const sidebarSelectedComponent: { [key: string]: JSX.Element } = {
    SEGMENTO: (
      <Segment segments={categoryData} isAdminData={showAdminExercises} />
    ),
    EQUIPAMENTO: (
      <Equipment equipments={categoryData} isAdminData={showAdminExercises} />
    ),
    ACESSÓRIO: (
      <Accessory accessories={categoryData} isAdminData={showAdminExercises} />
    ),
    'POSIÇÃO MÃOS / PÉS': (
      <HandPrint handPrints={categoryData} isAdminData={showAdminExercises} />
    ),
    POSTURA: (
      <Posture postures={categoryData} isAdminData={showAdminExercises} />
    ),
    'PRINCIPIO ATIVO': (
      <ActivePrinciple
        activePrinciples={categoryData}
        isAdminData={showAdminExercises}
      />
    ),
    'N° SÉRIES': (
      <NumberOfSeries series={categoryData} isAdminData={showAdminExercises} />
    ),
    'CARDIO AEROBICO': (
      <AerobicCardio
        aerobicCardios={categoryData}
        isAdminData={showAdminExercises}
      />
    ),
  };

  function NewSegmentModal() {
    const formRef = useRef<FormHandles>(null);

    const [loading, setLoading] = useState(false);
    const [defaultCategory, setDefaultCategory] = useState<SelectOption>(
      {} as SelectOption,
    );

    const categoryVariations = useMemo(
      () => [
        { value: 'SEGMENTO', label: 'Segmento' },
        {
          value: 'EQUIPAMENTO',
          label: 'Equipamento',
        },
        {
          value: 'ACESSÓRIO',
          label: 'Acessório',
        },
        {
          value: 'POSIÇÃO MÃOS / PÉS',
          label: 'Posição mãos/pés',
        },
        {
          value: 'POSTURA',
          label: 'Postura',
        },
        {
          value: 'N° SÉRIES',
          label: 'N° de séries',
        },
        {
          value: 'PRINCIPIO ATIVO',
          label: 'Princípio ativo',
        },
        {
          label: 'Cardio aeróbico',
          value: 'CARDIO AEROBICO',
        },
      ],
      [],
    );

    useEffect(() => {
      const filteredCategory = categoryVariations.filter(
        (category) => category.value === selectedComponent,
      )[0];

      setDefaultCategory(filteredCategory);
    }, [categoryVariations]);

    const handleSubmit = useCallback(async (data: FormData) => {
      setLoading(true);

      const schema = Yup.object().shape({
        category: Yup.string().required('O campo é obrigatório!'),
        description: Yup.string().required('O campo é obrigatório!'),
      });

      try {
        await schema.validate(data, { abortEarly: false });

        const { data: dataResponse } = await api.post(`training-stores`, data);
        toast.success('Item cadastrado com sucesso!');

        setNewModal(false);

        updateCategory(selectedComponent);
      } catch (err) {
        console.log(err);
        setLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
        }

        toast.error('Erro ao cadastrar item!');
      }
    }, []);

    return (
      <Modal isOpen={newModal} setOpen={setNewModal}>
        <CreateContainer>
          <h1>Novo Training</h1>

          <Form
            ref={formRef}
            onSubmit={handleSubmit}
            initialData={{ category: defaultCategory }}
          >
            <fieldset>
              <p>Tipo:</p>
              <Select
                name="category"
                placeholder="Selecionar"
                defaultValue={defaultCategory}
                options={categoryVariations}
                isSearchable={false}
                styles={{
                  placeholder: () => ({
                    padding: '0 1rem',
                    color: '#00000080',
                  }),
                  singleValue: () => ({
                    padding: '0 1rem',
                  }),
                }}
              />
            </fieldset>

            <fieldset>
              <p>Nome:</p>
              <Input
                name="description"
                fontSize="1.5rem"
                containerStyle={{
                  height: 38,
                  padding: '0 1.7rem',
                }}
              />
            </fieldset>
          </Form>

          <ButtonBlack
            disabled={loading}
            onClick={() => formRef.current?.submitForm()}
          >
            Adicionar
          </ButtonBlack>
        </CreateContainer>
      </Modal>
    );
  }

  const handleShowAdminExercises = () =>
    setShowAdminExercises(!showAdminExercises);

  return (
    <Layout title="Training - Store">
      <NewSegmentModal />

      <Container>
        <Sidebar>
          <ul>
            {sidebarList.map((item) => (
              <li
                key={item.value}
                onClick={() => setSelectedComponent(item.value)}
                className={selectedComponent === item.value ? 'active' : ''}
              >
                <p>{item.title}</p>
              </li>
            ))}
          </ul>
        </Sidebar>

        <Wrapper>
          <Header>
            <HeaderWrapper onClick={() => goBack()}>
              <IoIosArrowBack color="#FFDF22" size={24} />

              <HeaderLabel>Dados Técnicos</HeaderLabel>
            </HeaderWrapper>

            {!showAdminExercises && (
              <ButtonAdd onClick={() => setNewModal(true)}>Adicionar</ButtonAdd>
            )}

            <ButtonYellow onClick={handleShowAdminExercises}>
              {showAdminExercises ? 'Meus Dados' : 'Dados admin'}
            </ButtonYellow>
          </Header>

          <Content>{sidebarSelectedComponent[selectedComponent]}</Content>
        </Wrapper>
      </Container>
    </Layout>
  );
}
